import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Button,
  Badge,
  useToast,
  Input,
  Flex,
  Text,
  IconButton,
  useDisclosure,
  HStack,
  VStack,
  Skeleton,
  Select,
  Image,
  Tooltip,
  Switch,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
} from "@chakra-ui/react";
import { EditIcon, SearchIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { useDispatch, useSelector } from "react-redux";
import {
  getDataSuperAdmin,
  deleteDataSuperAdmin,
  updateDataSuperAdmin,
} from "../../../redux/actions/common.action";
import { useNavigate } from "react-router-dom";
import {
  Filter,
  RefreshCcw,
  Building2,
  Home,
  Plus,
  Eye,
  MapPin,
  ArrowLeft,
} from "lucide-react";
import Pagination from "../../proposal/components/Pagination ";
import DeleteConfirmationModal from "../../../components/DeleteConfirmationModal";

interface FooterContent {
  _id: string;
  companyName: string;
  companyDescription: string;
  officeName: string;
  address: string;
  country: {
    name: string;
    code: string;
    flag: string;
  };
  email: string;
  phone: string;
  companyLogo: string;
  socialMedia: {
    facebook: string;
    instagram: string;
    linkedin: string;
    twitter: string;
    youtube: string;
  };
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

const FooterContentList: React.FC = () => {
  const dispatch: any = useDispatch();
  const toast = useToast();
  const navigate = useNavigate();

  const {
    footerContents = [],
    footerContentPage = {
      currentPage: 1,
      totalPages: 1,
      totalDocs: 0,
      limit: 10,
    },
    loading,
  } = useSelector((state: any) => state.common);

  const [searchTerm, setSearchTerm] = useState("");
  const [countryFilter, setCountryFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageLimit, setPageLimit] = useState(10);
  const [deleteItem, setDeleteItem] = useState<{
    id: string;
    name: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const {
    isOpen: isDeleteModalOpen,
    onOpen: onDeleteModalOpen,
    onClose: onDeleteModalClose,
  } = useDisclosure();

  useEffect(() => {
    fetchFooterContents();
  }, [currentPage, pageLimit]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentPage(1);
      fetchFooterContents();
    }, 600);

    return () => clearTimeout(timer);
  }, [searchTerm, countryFilter, statusFilter]);

  const fetchFooterContents = async () => {
    let queryParams = `footer-content?page=${currentPage}&limit=${pageLimit}`;

    if (searchTerm) {
      queryParams += `&search=${searchTerm}`;
    }

    if (countryFilter) {
      queryParams += `&country=${countryFilter}`;
    }

    if (statusFilter !== "") {
      queryParams += `&isActive=${statusFilter}`;
    }

    await dispatch(getDataSuperAdmin(queryParams));
  };

  const handleDeleteClick = (id: string, name: string) => {
    setDeleteItem({ id, name });
    onDeleteModalOpen();
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    setIsDeleting(true);
    try {
      await dispatch(
        deleteDataSuperAdmin(deleteItem.id, "footer-content", toast)
      );
      fetchFooterContents();
      onDeleteModalClose();
      setDeleteItem(null);

      toast({
        title: "Deleted Successfully",
        description: "Footer content has been deleted",
        status: "success",
        duration: 3000,
        position: "top-right",
        isClosable: true,
      });
    } catch (error) {
      console.error("Error deleting footer content:", error);
      toast({
        title: "Error",
        description: "Failed to delete footer content",
        status: "error",
        duration: 3000,
        position: "top-right",
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCloseDeleteModal = () => {
    if (!isDeleting) {
      setDeleteItem(null);
      onDeleteModalClose();
    }
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await dispatch(
        updateDataSuperAdmin(
          id,
          "footer-content",
          { isActive: !currentStatus },
          navigate,
          toast
        )
      );
      fetchFooterContents();
    } catch (error) {
      console.error("Error toggling status:", error);
    }
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handlePageLimitChange = (newLimit: number) => {
    setPageLimit(newLimit);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setSearchTerm("");
    setCountryFilter("");
    setStatusFilter("");
    setCurrentPage(1);
    fetchFooterContents();
  };

  const getPaginationInfo = () => {
    if (
      !footerContents ||
      footerContents.length === 0 ||
      !footerContentPage?.totalDocs
    ) {
      return "Showing 0 to 0 of 0 items";
    }
    const startItem = (currentPage - 1) * pageLimit + 1;
    const endItem = Math.min(
      currentPage * pageLimit,
      footerContentPage.totalDocs
    );
    return `Showing ${startItem} to ${endItem} of ${footerContentPage.totalDocs} items`;
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (searchTerm) count++;
    if (countryFilter) count++;
    if (statusFilter !== "") count++;
    return count;
  };

  // Get unique countries from footer contents
  const uniqueCountries = Array.from(
    new Set(footerContents?.map((item: FooterContent) => item.country?.name))
  ).filter(Boolean);

  return (
    <Box p="8px" bg="gray.50">
      <style>
        {`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
          .refresh-icon-spin {
            animation: spin 1s linear infinite;
          }
          @keyframes slideInLeft {
            from {
              opacity: 0;
              transform: translateX(-20px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
          .slide-in-left {
            animation: slideInLeft 0.4s ease-out;
          }
        `}
      </style>

      {/* Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
        px={{ base: 3, md: 4 }}
        py={{ base: 2, md: 3 }}
        className="slide-in-left"
      >
        <Flex
          justify="space-between"
          align="center"
          flexWrap="wrap"
          gap={{ base: 2, md: 4 }}
        >
          {/* Left: Back Button + Breadcrumb */}
          <Flex
            flexDirection={{ base: "row", lg: "column" }}
            align={{ base: "center", lg: "flex-start" }}
            gap={{ base: 2, lg: 1 }}
          >
            {/* Responsive Back Button */}
            <Button
              leftIcon={<ArrowLeft size={16} />}
              onClick={() => navigate("/crm-super-admin-dashboard")}
              size={{ base: "xs", sm: "sm" }}
              variant="ghost"
              colorScheme="blue"
              fontWeight="medium"
              fontSize={{ base: "xs", sm: "sm" }}
              px={3}
              _hover={{
                bg: "blue.50",
                transform: "translateX(-4px)",
              }}
              transition="all 0.2s"
            >
              <Box display={{ base: "none", sm: "inline" }}>
                Back to Dashboard
              </Box>
            </Button>

            {/* Breadcrumb Navigation */}
            <Breadcrumb
              spacing={2}
              separator={<ChevronRightIcon color="gray.400" boxSize={3} />}
              fontSize={{ base: "xs", md: "sm" }}
              color="gray.600"
              display={{ base: "none", sm: "flex" }}
            >
              <BreadcrumbItem>
                <BreadcrumbLink
                  onClick={() => navigate("/")}
                  display="flex"
                  alignItems="center"
                  gap={1}
                  _hover={{ color: "blue.500" }}
                  transition="color 0.2s"
                >
                  <Home size={12} />
                  <Text>Dashboard</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem isCurrentPage>
                <BreadcrumbLink
                  display="flex"
                  alignItems="center"
                  gap={1}
                  color="blue.500"
                  fontWeight="medium"
                >
                  <Building2 size={12} />
                  <Text>Footer Content</Text>
                </BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
          </Flex>

          {/* Right: Page Title and Stats */}
          <HStack spacing={{ base: 2, md: 4 }} align="center">
            <VStack align="end" spacing={0}>
              <Text
                fontSize={{ base: "md", md: "lg" }}
                fontWeight="bold"
                color="gray.800"
              >
                Footer Content Management
              </Text>
              <Text fontSize={{ base: "xs", md: "sm" }} color="gray.500">
                Manage company information
              </Text>
            </VStack>

            <Box
              bgGradient="linear(to-br, orange.400, orange.600)"
              p={{ base: 2, md: 3 }}
              borderRadius="xl"
              boxShadow="md"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Building2 size={20} color="white" />
            </Box>
          </HStack>
        </Flex>
      </Box>

      {/* Search and Filter Header */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
        mb="8px"
      >
        <Flex
          mt={4}
          px={4}
          pb={4}
          gap={{ base: 2 }}
          justify="space-between"
          align="center"
          flexDirection={{ base: "column", lg: "row" }}
        >
          {/* Search Input */}
          <Box position="relative" maxW="md">
            <SearchIcon
              position="absolute"
              left="12px"
              top="50%"
              transform="translateY(-50%)"
              color="gray.400"
              w={4}
              h={4}
            />
            <Input
              w={{ base: "300px", lg: "400px" }}
              type="text"
              placeholder="Search by company, office, country..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              pl="40px"
              pr={4}
              py={2}
              fontSize="xs"
              border="1px"
              borderColor="gray.300"
              borderRadius="lg"
              _focus={{
                outline: "none",
                ring: 1,
                ringColor: "blue.500",
                borderColor: "blue.500",
              }}
            />
          </Box>

          {/* Action Buttons */}
          <HStack spacing={2}>
            <Button
              size="sm"
              leftIcon={<Plus size={14} />}
              onClick={() => navigate("/create-footer-content")}
              fontSize="sm"
              fontWeight="medium"
              color="white"
              bg="orange.500"
              _hover={{ bg: "orange.600" }}
              px={{ base: 2, lg: 4 }}
              py="4px"
              borderRadius="lg"
            >
              Add New
            </Button>

            <Button
              size="sm"
              onClick={() => setShowFilters(!showFilters)}
              fontSize="xs"
              fontWeight="medium"
              color="gray.700"
              bg="white"
              border="1px"
              borderColor="gray.300"
              _hover={{ bg: "gray.50" }}
              px={{ base: 2, lg: 3 }}
              py={2}
              borderRadius="lg"
              leftIcon={<Filter size={12} />}
              position="relative"
            >
              Filters
              {getActiveFiltersCount() > 0 && (
                <Badge
                  position="absolute"
                  top="-8px"
                  right="-8px"
                  borderRadius="full"
                  bg="blue.500"
                  color="white"
                  fontSize="10px"
                  minW="20px"
                  h="20px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  fontWeight="bold"
                >
                  {getActiveFiltersCount()}
                </Badge>
              )}
            </Button>

            <Button
              size="sm"
              onClick={handleClearFilters}
              isDisabled={loading}
              fontSize="xs"
              fontWeight="medium"
              color="gray.700"
              bg="white"
              border="1px"
              borderColor="gray.300"
              _hover={!loading ? { bg: "gray.50" } : {}}
              px={{ base: 2, lg: 3 }}
              py={2}
              borderRadius="lg"
              title="Refresh data and clear filters"
            >
              <Box
                as={RefreshCcw}
                size={14}
                mr={2}
                className={loading ? "refresh-icon-spin" : ""}
              />
              Refresh
            </Button>
          </HStack>
        </Flex>
      </Box>

      {/* Filters Panel */}
      {showFilters && (
        <Box
          p={4}
          bg="gray.50"
          border="1px"
          borderColor="gray.200"
          borderRadius="lg"
          mb="8px"
        >
          <HStack spacing={3}>
            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                Country
              </Text>
              <Select
                value={countryFilter}
                onChange={(e) => setCountryFilter(e.target.value)}
                fontSize="xs"
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
                px={2}
                py={1}
                size="sm"
                _focus={{
                  outline: "none",
                  ring: 1,
                  ringColor: "blue.500",
                }}
              >
                <option value="">All Countries</option>
                {uniqueCountries.map((country: any) => (
                  <option key={country} value={country}>
                    {country}
                  </option>
                ))}
              </Select>
            </Box>

            <Box>
              <Text fontSize="xs" fontWeight="medium" color="gray.600" mb={1}>
                Status
              </Text>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                fontSize="xs"
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
                px={2}
                py={1}
                size="sm"
                _focus={{
                  outline: "none",
                  ring: 1,
                  ringColor: "blue.500",
                }}
              >
                <option value="">All Status</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </Select>
            </Box>
          </HStack>
        </Box>
      )}

      {/* Table */}
      <Box
        bg="white"
        borderRadius="xl"
        boxShadow="sm"
        border="1px"
        borderColor="gray.200"
      >
        <Box overflowX="auto">
          <Table variant="simple" size="sm">
            <Thead bg="gray.50" borderBottom="1px" borderColor="gray.200">
              <Tr>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Company
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Office Name
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Country
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Contact
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Status
                </Th>
                <Th
                  px={3}
                  py={3}
                  fontSize="xs"
                  fontWeight="medium"
                  color="gray.600"
                  textTransform="uppercase"
                  letterSpacing="wider"
                >
                  Actions
                </Th>
              </Tr>
            </Thead>
            <Tbody borderTop="1px" borderColor="gray.200">
              {loading ? (
                [...Array(pageLimit)].map((_, index) => (
                  <Tr key={index}>
                    {[...Array(6)].map((_, cellIndex) => (
                      <Td key={cellIndex} px={3} py={3}>
                        <Skeleton height="16px" borderRadius="md" />
                      </Td>
                    ))}
                  </Tr>
                ))
              ) : footerContents?.length > 0 ? (
                footerContents.map((content: FooterContent) => (
                  <Tr
                    key={content._id}
                    _hover={{ bg: "gray.50" }}
                    borderBottom="1px"
                    borderColor="gray.200"
                  >
                    <Td px={3} py={3}>
                      <HStack spacing={2}>
                        <Image
                          src={content.companyLogo}
                          alt={content.companyName}
                          boxSize="32px"
                          objectFit="contain"
                          borderRadius="md"
                        />
                        <Text
                          fontSize="xs"
                          fontWeight="medium"
                          color="gray.900"
                          noOfLines={1}
                        >
                          {content.companyName}
                        </Text>
                      </HStack>
                    </Td>
                    <Td px={3} py={3}>
                      <Text fontSize="xs" color="gray.700">
                        {content.officeName}
                      </Text>
                    </Td>
                    <Td px={3} py={3}>
                      <HStack spacing={2}>
                        <Image
                          src={content.country.flag}
                          alt={content.country.name}
                          boxSize="20px"
                          borderRadius="sm"
                        />
                        <Text fontSize="xs" color="gray.700">
                          {content.country.name}
                        </Text>
                      </HStack>
                    </Td>
                    <Td px={3} py={3}>
                      <VStack align="start" spacing={0}>
                        <Text fontSize="xs" color="gray.700" noOfLines={1}>
                          {content.email}
                        </Text>
                        <Text fontSize="xs" color="gray.500">
                          {content.phone}
                        </Text>
                      </VStack>
                    </Td>
                    <Td px={3} py={3}>
                      <Switch
                        size="sm"
                        isChecked={content.isActive}
                        onChange={() =>
                          handleToggleStatus(content._id, content.isActive)
                        }
                        colorScheme="green"
                      />
                    </Td>
                    <Td px={3} py={3}>
                      <HStack spacing={1}>
                        <Tooltip label="Edit" placement="top">
                          <IconButton
                            aria-label="Edit"
                            icon={<EditIcon />}
                            size="sm"
                            variant="ghost"
                            colorScheme="blue"
                            onClick={() =>
                              navigate(`/edit-footer-content/${content._id}`)
                            }
                            p="6px"
                            minW="auto"
                            h="auto"
                            _hover={{ bg: "blue.50" }}
                          />
                        </Tooltip>
                        <Tooltip label="Delete" placement="top">
                          <IconButton
                            aria-label="Delete"
                            icon={<Building2 size={16} />}
                            size="sm"
                            variant="ghost"
                            colorScheme="red"
                            onClick={() =>
                              handleDeleteClick(
                                content._id,
                                `${content.companyName} - ${content.officeName}`
                              )
                            }
                            p="6px"
                            minW="auto"
                            h="auto"
                            _hover={{ bg: "red.50" }}
                          />
                        </Tooltip>
                      </HStack>
                    </Td>
                  </Tr>
                ))
              ) : (
                <Tr>
                  <Td colSpan={6} px={3} py={16} textAlign="center">
                    <VStack spacing={4}>
                      <Building2 size={64} color="#d1d5db" />
                      <VStack spacing={2}>
                        <Text
                          fontSize="lg"
                          fontWeight="medium"
                          color="gray.700"
                        >
                          No footer content found
                        </Text>
                        <Text fontSize="sm" color="gray.500">
                          Click "Add New" to create footer content
                        </Text>
                      </VStack>
                    </VStack>
                  </Td>
                </Tr>
              )}
            </Tbody>
          </Table>
        </Box>

        {/* Pagination */}
        <Flex justify="space-between" align="center" px={2} py={2}>
          <HStack spacing={4}>
            <Text fontSize="xs" color="gray.600" fontWeight="medium">
              {getPaginationInfo()}
            </Text>
          </HStack>

          <HStack spacing={4}>
            <HStack spacing={2}>
              <Text fontSize="xs" color="gray.600" fontWeight="medium">
                Show
              </Text>
              <Select
                value={pageLimit}
                onChange={(e) =>
                  handlePageLimitChange(parseInt(e.target.value))
                }
                size="xs"
                bg="white"
                border="1px"
                borderColor="gray.300"
                borderRadius="md"
                px={3}
                py={1}
                fontSize="xs"
                fontWeight="medium"
                color="gray.700"
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  outline: "none",
                  ring: 1,
                  ringColor: "blue.500",
                  borderColor: "blue.500",
                }}
                w="100px"
              >
                <option value={5}>5</option>
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={50}>50</option>
                <option value={100}>100</option>
              </Select>
              <Text fontSize="xs" color="gray.600">
                per page
              </Text>
            </HStack>

            {footerContentPage?.totalPages > 1 && (
              <Pagination
                currentPage={currentPage}
                totalPages={footerContentPage.totalPages}
                onPageChange={handlePageChange}
              />
            )}
          </HStack>
        </Flex>
      </Box>

      {/* Delete Confirmation Modal */}
      <DeleteConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={confirmDelete}
        title="Delete Footer Content"
        itemName={deleteItem?.name || ""}
        itemType="footer content"
        description="This will remove the company information from the public website."
        isDeleting={isDeleting}
        warningMessage="This action will soft delete the footer content. It will be hidden from the website but can be restored by an administrator if needed."
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
      />
    </Box>
  );
};

export default FooterContentList;
